import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, Phone, MapPin, CheckCircle, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  { value: "cours-particuliers", label: "Cours Particuliers" },
  { value: "cours-collectifs", label: "Cours Collectifs" },
  { value: "stages-revisions", label: "Stages de Révisions" },
  { value: "english-class", label: "English Class" },
  { value: "scolarite-atypique", label: "Scolarité Atypique" },
  { value: "partenariat-entreprise", label: "Partenariat Entreprise" },
  { value: "autre", label: "Autre" }
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      toast.success("Message envoyé avec succès ! Nous vous répondrons rapidement.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_interest: "",
        subject: "",
        message: ""
      });
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      // Error is already shown to user via toast
      toast.error("Erreur lors de l'envoi du message. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-14 md:py-28 relative bg-white"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block text-blue-600 font-semibold mb-4 tracking-wider uppercase text-sm bg-blue-50 px-4 py-2 rounded-full">
            Contact
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold text-blue-900 mb-4 md:mb-6">
            Demandez votre devis gratuit
          </h2>
          <p className="text-base md:text-lg text-blue-800/70 max-w-2xl mx-auto">
            Remplissez le formulaire ci-dessous et nous vous répondrons 
            dans les plus brefs délais.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-blue-900 mb-6">
                Nos coordonnées
              </h3>
              
              <div className="space-y-5">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-blue-900 font-semibold mb-1">Adresse</h4>
                    <p className="text-blue-700/70">23 rue de Goupillières</p>
                    <p className="text-blue-700/70">78910 FLEXANVILLE</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-blue-900 font-semibold mb-1">Téléphone</h4>
                    <a 
                      href="tel:+33664446823" 
                      className="text-blue-700/70 hover:text-orange-500 transition-colors"
                    >
                      06 64 44 68 23
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="text-blue-900 font-semibold mb-1">Email</h4>
                    <a 
                      href="mailto:contact@potentielplus.net" 
                      className="text-blue-700/70 hover:text-orange-500 transition-colors"
                    >
                      contact@potentielplus.net
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="bg-blue-50 rounded-2xl p-6">
              <h4 className="text-blue-900 font-semibold mb-4">Horaires d'ouverture</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700/70">Lundi - Vendredi</span>
                  <span className="text-blue-900 font-medium">9h00 - 19h00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700/70">Samedi</span>
                  <span className="text-blue-900 font-medium">9h00 - 17h00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700/70">Dimanche</span>
                  <span className="text-blue-700/50">Fermé</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-3xl p-5 sm:p-6 md:p-10 border-2 border-blue-100 shadow-card">
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-blue-900 mb-2">Message envoyé !</h3>
                  <p className="text-blue-700/70">Nous vous répondrons dans les plus brefs délais.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                  {/* Name & Email Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-blue-900 mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        autoComplete="name"
                        data-testid="contact-name-input"
                        className="form-input w-full bg-blue-50/50 border border-blue-200 text-blue-900 rounded-xl h-12 px-4 transition-all focus:bg-white"
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-blue-900 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        autoComplete="email"
                        inputMode="email"
                        data-testid="contact-email-input"
                        className="form-input w-full bg-blue-50/50 border border-blue-200 text-blue-900 rounded-xl h-12 px-4 transition-all focus:bg-white"
                        placeholder="votre@email.fr"
                      />
                    </div>
                  </div>

                  {/* Phone & Service Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-blue-900 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                        inputMode="tel"
                        data-testid="contact-phone-input"
                        className="form-input w-full bg-blue-50/50 border border-blue-200 text-blue-900 rounded-xl h-12 px-4 transition-all focus:bg-white"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                    <div>
                      <label htmlFor="service_interest" className="block text-sm font-medium text-blue-900 mb-2">
                        Service souhaité
                      </label>
                      <div className="relative">
                        <select
                          id="service_interest"
                          name="service_interest"
                          value={formData.service_interest}
                          onChange={handleChange}
                          data-testid="contact-service-select"
                          className="form-input w-full bg-blue-50/50 border border-blue-200 text-blue-900 rounded-xl h-12 pl-4 pr-10 transition-all appearance-none cursor-pointer focus:bg-white"
                        >
                          <option value="">Sélectionnez un service</option>
                          {serviceOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="w-5 h-5 text-blue-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-blue-900 mb-2">
                      Sujet *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      data-testid="contact-subject-input"
                      className="form-input w-full bg-blue-50/50 border border-blue-200 text-blue-900 rounded-xl h-12 px-4 transition-all focus:bg-white"
                      placeholder="Objet de votre message"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-blue-900 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-message-input"
                      className="form-input w-full bg-blue-50/50 border border-blue-200 text-blue-900 rounded-xl p-4 transition-all resize-none focus:bg-white"
                      placeholder="Décrivez vos besoins..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="btn-primary w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Envoyer le message</span>
                      </>
                    )}
                  </button>

                  <p className="text-xs text-blue-700/50 text-center">
                    En soumettant ce formulaire, vous acceptez notre politique de confidentialité.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
